import { useAppStore } from '@runtime/core/store/appStore'

import { ASSETS } from '@runtime/core/theme/assets'
import {
  DEFAULT_POSTER_THEME_ID,
  getThemePuzzlePosterTileUrls,
  getThemePuzzlePreviews,
  resolveTheme,
} from '@runtime/core/theme/posterThemes'
import {
  hasCachedThemeAssets,
  isOffline,
} from '@runtime/core/theme/themeEngineCache'
import { applyThemePlayerColors } from '@runtime/core/theme/themeEnginePlayers'
import type {
  PosterThemeConfig,
  ThemePalette,
  ThemeTiles,
} from '@runtime/core/theme/themeEngineShared'

// Fallback: default poster theme.
export const US_POSTER_DEFAULT = DEFAULT_POSTER_THEME_ID

export type ThemeEngineApi = {
  applyFromConfig: (cfg: PosterThemeConfig) => void
  restore: (shouldRestore?: () => boolean) => Promise<void>
  current: () => string | null
}

type ThemeWindow = Window &
  typeof globalThis & {
    __i18nBump?: () => void
    ThemeEngine?: ThemeEngineApi
  }

function setCssVar(name: string, val: string | null | undefined) {
  if (!val) return
  try {
    document.documentElement.style.setProperty(name, String(val))
  } catch {
    // ignore
  }
}

function applyThemePaletteVars(palette: ThemePalette | undefined) {
  const p = palette || {}

  setCssVar('--bg', p.surface)
  setCssVar('--panel', p.surfaceVariant)
  setCssVar('--panel2', p.surfaceVariant)

  const accentPrimary = p.primary || p.secondary
  const accentSecondary = p.secondary || p.primary || accentPrimary

  setCssVar('--accent', accentPrimary)
  setCssVar('--accent-secondary', accentSecondary)

  setCssVar('--grid', p.border)

  try {
    if (p.text) {
      document.body && (document.body.style.color = p.text)
    }
  } catch {
    // ignore
  }
}

function applyThemeTiles(tiles: ThemeTiles | undefined) {
  try {
    if (tiles?.center) {
      ASSETS.center = tiles.center
    }
    if (Array.isArray(tiles?.set)) {
      for (let i = 0; i < 6; i += 1) {
        if (tiles.set[i]) ASSETS['t' + (i + 1)] = tiles.set[i]
      }
    }
  } catch (e) {
    console.warn('[POSTER] ASSETS update failed', e)
  }
}

function getThemeWindow(): ThemeWindow | null {
  if (typeof window === 'undefined') return null
  return window as ThemeWindow
}

function bumpThemeI18n(): void {
  try {
    getThemeWindow()?.__i18nBump?.()
  } catch {
    // ignore
  }
}

// ---- Theme application engine ----
export const ThemeEngine: ThemeEngineApi = (function () {
  let currentId: string | null = null
  let revision = 0

  function applyFromConfig(cfg: PosterThemeConfig): void {
    if (!cfg || typeof cfg !== 'object') return
    revision += 1
    currentId = cfg.id || cfg.name || null

    applyThemePaletteVars(cfg.palette)
    applyThemePlayerColors(cfg, bumpThemeI18n)
    applyThemeTiles(cfg.tiles)
    bumpThemeI18n()

    try {
      useAppStore.getState().bumpThemeRev()
    } catch {
      // ignore
    }
  }

  function current(): string | null {
    return currentId
  }

  async function restore(
    shouldRestore: () => boolean = () => true,
  ): Promise<void> {
    try {
      const id = useAppStore.getState().themeId || US_POSTER_DEFAULT
      const requestRevision = ++revision
      const current = () =>
        shouldRestore() &&
        revision === requestRevision &&
        (useAppStore.getState().themeId || US_POSTER_DEFAULT) === id
      if (!id || !current()) return
      if (id !== US_POSTER_DEFAULT && isOffline()) {
        const cached = await hasCachedThemeAssets(id)
        if (!current()) return
        if (!cached) {
          const fallbackCfg = await resolveTheme(US_POSTER_DEFAULT)
          if (!current()) return
          useAppStore.getState().setThemeId(US_POSTER_DEFAULT)
          if (fallbackCfg) applyFromConfig(fallbackCfg)
          return
        }
      }

      const cfg = await resolveTheme(id)
      if (!current()) return
      if (cfg) {
        applyFromConfig(cfg)
        return
      }

      if (id !== US_POSTER_DEFAULT && isOffline()) {
        const fallbackCfg = await resolveTheme(US_POSTER_DEFAULT)
        if (current() && fallbackCfg) applyFromConfig(fallbackCfg)
      }
    } catch (e) {
      console.warn('[POSTER] restore failed', e)
    }
  }

  return { applyFromConfig, restore, current }
})()

// Optional: expose globally for debugging.
const themeWindow = getThemeWindow()
if (themeWindow) {
  themeWindow.ThemeEngine = ThemeEngine
}

export function getCurrentThemeIdOrDefault() {
  try {
    const id = ThemeEngine.current()
    if (id) return id
  } catch {
    // ignore
  }
  try {
    const id = useAppStore.getState().themeId
    if (id) return id
  } catch {
    // ignore
  }
  return US_POSTER_DEFAULT
}

// Return the list of 25 poster tile src values for the current theme.
export function getPosterPuzzleTiles() {
  const themeId = getCurrentThemeIdOrDefault()
  return getThemePuzzlePosterTileUrls(themeId)
}

// Puzzle home previews (3 thumbnails).
export function getPuzzleHomeUnitedPreview() {
  const themeId = getCurrentThemeIdOrDefault()
  return getThemePuzzlePreviews(themeId).united
}

export function getPuzzleHomePosterPreview() {
  const themeId = getCurrentThemeIdOrDefault()
  return getThemePuzzlePreviews(themeId).poster
}

export function getPuzzleHomePosterSwitchPreview() {
  const themeId = getCurrentThemeIdOrDefault()
  return getThemePuzzlePreviews(themeId).switch
}

// Preview for the "Poster Puzzle" tile on the Puzzle home.
export function getPosterPuzzlePreview() {
  return getPuzzleHomePosterPreview()
}
